import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    DialogContent,
    List,
    ListItem,
    Stack,
    Typography,
    Box,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ExpandMore } from "@mui/icons-material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IPlayerProps, Player } from "@lottiefiles/react-lottie-player"
import { faAddressCard, faInfoCircle, faShoppingBag, IconDefinition } from "@fortawesome/pro-light-svg-icons"
import { LocalizedEnum, LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { StandardLoadingButton } from "../../buttons/LoadingButton"
import { StandardButton } from "../../buttons/StandardButton"
import { ExternalLink } from "../../links/ExternalLink"
import { StandardDialog } from "../StandardDialog"
import { PIIItem } from "./consentDialogTypes"

const useStyles = makeStyles()((theme) => ({
    textColor: {
        color: theme.palette.text.primary,
    },
    lightColor: {
        color: theme.palette.text.secondary,
    },
    contentWrap: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        paddingBottom: "1.5rem",
        paddingTop: "0",
    },
    accordionRoot: {
        backgroundColor: "transparent",
        "&::before": {
            backgroundColor: "transparent",
        },
    },
    // HACK: Have to add !important to beat specificity of expanded styles
    accordionSummaryRoot: {
        minHeight: "0 !important",
        paddingLeft: 0,
    },
    accordionSummaryContent: {
        marginTop: "0 !important",
        marginBottom: "0 !important",
    },
    accordionDetailsRoot: {
        paddingTop: "1rem",
        paddingLeft: 0,
        paddingBottom: 0,
    },
    listItem: {
        flexDirection: "column",
        alignItems: "flex-start",
    },
    listItemPadding: {
        padding: 0,
    },
    categoryTitleWrap: {
        display: "grid",
        gridTemplateColumns: "1rem 1fr",
        gap: "0.5rem",
    },
    piiContainer: {
        display: "grid",
        gap: "0.5rem",
        padding: 0,
    },
    privacyPolicyLink: {
        marginTop: "0.5rem",
    },
    piiItems: {
        paddingLeft: "1.5rem",
    },
    link: {
        color: "#2196F3",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    button: {
        borderRadius: "0.5rem",
        border: `1px solid ${theme.palette.tailwind.slate[700]}`,
    },
    player: {
        height: "15rem",
    },
}))

type CategoryMap = {
    [key: string]: {
        // Defining an index signature
        icon: IconDefinition
        items: PIIItem[]
    }
}

const categories: CategoryMap = {
    CONTACT_INFO: {
        icon: faAddressCard,
        items: [PIIItem.Name, PIIItem.EmailAddress, PIIItem.PhoneNumber],
    },
    IDENTIFIERS: {
        icon: faInfoCircle,
        items: [PIIItem.UserId, PIIItem.DeviceId],
    },
    USER_CONTENT: {
        icon: faShoppingBag,
        items: [PIIItem.CustomerSupport],
    },
}
const items = Object.entries(categories)
    .flatMap(([key, value]) => value.items.map((item) => ({ item, category: key })))
    .reduce((map, item) => map.set(item.item, item.category), new Map<PIIItem, string>())

type ConsentDialogProps = Readonly<{
    name: string
    title: string
    message: string
    piiItems: PIIItem[]
    privacyPolicyUrl: string
    skipLabel: string
    consentLabel: string
    lottieAnim: IPlayerProps["src"]
    open: boolean
    onClose: () => void
    savingConsent: boolean
    onButtonClick: (consented: boolean) => void
}>

export function ConsentDialog({
    name,
    title,
    message,
    piiItems,
    privacyPolicyUrl,
    skipLabel,
    consentLabel,
    lottieAnim,
    open,
    onClose,
    savingConsent,
    onButtonClick,
}: ConsentDialogProps) {
    const { classes, cx } = useStyles()

    const renderCategories = piiItems.reduce((map, item) => {
        const category = items.get(item)
        if (category === undefined) throw new Error(`No category found for PII item: ${item}`)
        if (map.has(category)) map.get(category)?.push(item)
        else map.set(category, [item])
        return map
    }, new Map<string, PIIItem[]>())

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
            dialogTitle={<Typography variant="h4">{title}</Typography>}
            aria-labelledby={`${name}-consent-dialog-title`}
            aria-describedby={`${name}-consent-dialog-message`}
        >
            <DialogContent className={classes.contentWrap}>
                <Player autoplay loop controls={false} src={lottieAnim} className={classes.player} />
                <Stack direction="column" spacing="1rem">
                    <Typography variant="body2" className={classes.textColor}>
                        {message}
                    </Typography>
                    <Accordion square elevation={0} classes={{ root: classes.accordionRoot }}>
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            classes={{ root: classes.accordionSummaryRoot, content: classes.accordionSummaryContent }}
                        >
                            <LocalizedStrict id="consent-dialog-privacy-policy">
                                <Typography variant="h6" className={classes.textColor}>
                                    Privacy
                                </Typography>
                            </LocalizedStrict>
                        </AccordionSummary>
                        <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>
                            <List className={classes.piiContainer}>
                                {[...renderCategories.entries()].map(([category, items], index) => (
                                    <ListItem key={index} className={cx(classes.listItem, classes.listItemPadding)}>
                                        <Stack direction="row" className={classes.categoryTitleWrap}>
                                            <FontAwesomeIcon icon={categories[category].icon} />
                                            <LocalizedEnum base="consent-dialog-pii-category" enumValue={category}>
                                                <Typography variant="body2" className={classes.textColor}>
                                                    {category}
                                                </Typography>
                                            </LocalizedEnum>
                                        </Stack>
                                        <List className={cx(classes.piiContainer, classes.piiItems)}>
                                            {items.map((item, innerIndex) => (
                                                <ListItem key={innerIndex} className={classes.listItemPadding}>
                                                    <LocalizedEnum base="consent-dialog-pii-item" enumValue={item}>
                                                        <Typography variant="body2" className={classes.lightColor}>
                                                            {item}
                                                        </Typography>
                                                    </LocalizedEnum>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </ListItem>
                                ))}
                            </List>
                            <Box className={classes.privacyPolicyLink}>
                                <LocalizedStrict id="consent-dialog-view-company-privacy-policy">
                                    <ExternalLink href={privacyPolicyUrl} className={classes.link}>
                                        <Typography variant="body1">View company privacy policy</Typography>
                                    </ExternalLink>
                                </LocalizedStrict>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Stack>

                <Stack direction="column" spacing="0.5rem">
                    <StandardLoadingButton
                        color="black"
                        loading={savingConsent}
                        onClick={() => onButtonClick(true)}
                        updateDefaultClasses={classes.button}
                    >
                        {consentLabel}
                    </StandardLoadingButton>
                    <StandardButton size="small" color="white" variant="contained" onClick={() => onButtonClick(false)}>
                        {skipLabel}
                    </StandardButton>
                </Stack>
            </DialogContent>
        </StandardDialog>
    )
}
